// AID 分配顯示層模型(S31,IA specs/01 §2.2;common/12 v1.1 §4.2.1)。
// 狀態 enum 值來自後端 wire(active/exhausted/revoked),顯示層全中文;計數推導與
// 表單前驗皆為純函式(便於單測)。
//
// RBAC(IA §3.2 + RBAC 矩陣 §3.4 第 122 列):
//   allocation 建立/調整/撤銷 = platform-owner + admin(security-officer 唯讀、
//   cs-readonly/ai-ops 無存取)。因此本域「寫」守門 canAidAllocWrite 刻意排除
//   security-officer —— 與 nav write 旗標(canAdminWrite 含 security-officer,讓其
//   看得到唯讀畫面)分工:nav 決定「看不看得到」、本函式決定「能不能動手」。
//   前端守門只影響 UI,伺服端 requireAdminWrite 才是真正邊界。
//
// ⚠️ allocation 撤銷「不是」dual-control 點(dual-control = AID revoke S28 / Plan
// publish / 大額帳務,見 IA §3.4 註 1)。撤銷只收回未 Activate 的 headroom,已
// Activate 的 AID 不受影響(狀態真相在 device-ca);採 reason/case + 二段確認。

import type { AidAllocation } from './aidAllocationApi';

// ---- 域級寫守門 ----

/** allocation 建立/調整/撤銷:admin + platform-owner(security-officer 唯讀)。 */
export function canAidAllocWrite(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'platform-owner';
}

// ---- owner_scope(workspace 綁定;08 §4.6#6 = user#{id} | org#{id}) ----

const OWNER_SCOPE_RE = /^(user|org)#[^\s#]+$/;

/** owner_scope 必須是 user#{id} 或 org#{id}(對齊後端 validOwnerScope,fail closed)。 */
export function validOwnerScope(scope: string | null | undefined): boolean {
  return typeof scope === 'string' && OWNER_SCOPE_RE.test(scope.trim());
}

/** owner_scope 顯示:user#… → 使用者、org#… → 組織 + 截斷 id。 */
export function ownerScopeLabel(scope: string | undefined): string {
  if (!scope) return '—';
  const [kind] = scope.split('#');
  if (kind === 'user') return `使用者 ${scope}`;
  if (kind === 'org') return `組織 ${scope}`;
  return scope;
}

// ---- level/model scope CSV 解析(表單輸入 → 去重字串陣列) ----

/** 把逗號 / 空白 / 換行分隔的輸入切成去重、非空的 scope 陣列(保留輸入順序)。 */
export function parseScopeCsv(input: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of input.split(/[\s,]+/)) {
    const v = raw.trim();
    if (v && !seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

// ---- 狀態顯示 ----

export function allocStatusLabel(status?: string): string {
  if (status === 'active') return '生效中';
  if (status === 'exhausted') return '已用罄';
  if (status === 'revoked') return '已撤銷';
  return status || '未知';
}

export function allocStatusTone(status?: string): string {
  if (status === 'active') return 'pill-good';
  if (status === 'exhausted') return 'pill-neutral';
  if (status === 'revoked') return 'pill-warn';
  return 'pill-neutral';
}

/** 撤銷可用條件:非已撤銷(active / exhausted 皆可收回剩餘 headroom)。 */
export function canRevokeAllocation(a: Pick<AidAllocation, 'status'>): boolean {
  return a.status !== 'revoked';
}

/** 調整可用條件:僅 active / exhausted(已撤銷不可再改;後端對 revoked 回 409)。 */
export function canAdjustAllocation(a: Pick<AidAllocation, 'status'>): boolean {
  return a.status !== 'revoked';
}

// ---- 計數(不變式 consumed ≤ allocated ≤ quantity) ----

/** 尚可分配(quantity − allocated)。 */
export function headroom(a: Pick<AidAllocation, 'quantity' | 'allocated'>): number {
  return Math.max(0, (a.quantity || 0) - (a.allocated || 0));
}

/** 已 earmark 但尚未 Activate(allocated − consumed);撤銷時被收回的部分。 */
export function unconsumed(a: Pick<AidAllocation, 'allocated' | 'consumed'>): number {
  return Math.max(0, (a.allocated || 0) - (a.consumed || 0));
}

/** allocated / quantity 百分比(0–100,quantity=0 回 0)。 */
export function allocatedPct(a: Pick<AidAllocation, 'quantity' | 'allocated'>): number {
  if (!a.quantity) return 0;
  return Math.min(100, Math.round(((a.allocated || 0) / a.quantity) * 100));
}

/** consumed / quantity 百分比(0–100)。 */
export function consumedPct(a: Pick<AidAllocation, 'quantity' | 'consumed'>): number {
  if (!a.quantity) return 0;
  return Math.min(100, Math.round(((a.consumed || 0) / a.quantity) * 100));
}

export interface CounterInput {
  quantity: number;
  allocated: number;
  consumed: number;
}

/**
 * 驗證計數不變式(consumed ≥ 0 且 consumed ≤ allocated ≤ quantity,quantity ≥ 1)。
 * 回傳中文錯誤字串或 null。與後端 ErrAidAllocInvalid 前驗一致(伺服端仍是真正邊界)。
 */
export function validateCounters({ quantity, allocated, consumed }: CounterInput): string | null {
  if (!Number.isInteger(quantity) || quantity < 1) return '總量(quantity)須為 ≥ 1 的整數。';
  if (!Number.isInteger(allocated) || allocated < 0) return '已分配(allocated)須為 ≥ 0 的整數。';
  if (!Number.isInteger(consumed) || consumed < 0) return '已耗用(consumed)須為 ≥ 0 的整數。';
  if (allocated > quantity) return '已分配不得超過總量(allocated ≤ quantity)。';
  if (consumed > allocated) return '已耗用不得超過已分配(consumed ≤ allocated)。';
  return null;
}

// ---- 建立表單前驗 ----

export interface CreateFormInput {
  batchRef: string;
  poRef: string;
  ownerScope: string;
  levelScope: string[];
  modelScope: string[];
  quantity: number;
}

/**
 * 建立表單客戶端前驗(對齊後端 CreateAllocation 的欄位檢查)。回傳中文錯誤字串或 null。
 * ⚠️ 這只是「送出前不白打一趟」的前驗;PO ceiling / batch 未 release / scope 超出 PO
 * 等真正的 fail-closed 判定在後端(422),UI 誠實呈現後端錯誤,不在前端假裝通過。
 */
export function validateCreateForm(f: CreateFormInput): string | null {
  if (!f.batchRef.trim()) return '批次參照(batch_ref)必填。';
  if (!f.poRef.trim()) return 'PO 參照(po_ref)必填。';
  if (!validOwnerScope(f.ownerScope)) return 'workspace(owner_scope)須為 user#{id} 或 org#{id} 格式。';
  if (f.levelScope.length === 0) return 'AID Level scope 至少一項。';
  if (f.modelScope.length === 0) return 'model scope 至少一項。';
  if (!Number.isInteger(f.quantity) || f.quantity < 1) return '總量(quantity)須為 ≥ 1 的整數。';
  return null;
}

// ---- 清單統計 ----

export interface AllocationSummary {
  total: number;
  active: number;
  exhausted: number;
  revoked: number;
  totalQuantity: number;
  totalAllocated: number;
  totalConsumed: number;
}

export function summarizeAllocations(items: AidAllocation[]): AllocationSummary {
  const s: AllocationSummary = {
    total: items.length,
    active: 0,
    exhausted: 0,
    revoked: 0,
    totalQuantity: 0,
    totalAllocated: 0,
    totalConsumed: 0,
  };
  for (const a of items) {
    if (a.status === 'active') s.active += 1;
    else if (a.status === 'exhausted') s.exhausted += 1;
    else if (a.status === 'revoked') s.revoked += 1;
    s.totalQuantity += a.quantity || 0;
    s.totalAllocated += a.allocated || 0;
    s.totalConsumed += a.consumed || 0;
  }
  return s;
}

export const ALLOC_STATUS_OPTIONS = [
  { value: '', label: '全部狀態' },
  { value: 'active', label: '生效中' },
  { value: 'exhausted', label: '已用罄' },
  { value: 'revoked', label: '已撤銷' },
] as const;
